// Invite token generation and management
import { nanoid } from 'nanoid';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { InviteToken } from '../types';

/**
 * Generate a new invite token
 */
export const generateInviteToken = async (
  target: InviteToken['target'],
  issuedBy: string,
  options: {
    oneTime?: boolean;
    expiresIn?: number; // seconds
    metadata?: InviteToken['metadata'];
  } = {}
): Promise<string> => {
  const token = nanoid(12); // 12-character token
  const expiresAt = options.expiresIn
    ? new Date(Date.now() + options.expiresIn * 1000)
    : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // Default 7 days

  const inviteToken: Omit<InviteToken, 'createdAt'> = {
    token,
    target,
    issuedBy,
    expiresAt,
    oneTime: options.oneTime ?? true,
    ...(options.metadata && { metadata: options.metadata }),
  };

  const inviteData: any = {
    token,
    target,
    issuedBy,
    expiresAt: Timestamp.fromDate(expiresAt),
    oneTime: options.oneTime ?? true,
    createdAt: serverTimestamp(),
  };

  // Only include metadata if it's provided and has valid (non-undefined/null) values
  // Use JSON.parse(JSON.stringify()) to deeply remove undefined values
  if (options.metadata && typeof options.metadata === 'object') {
    try {
      // First pass: remove undefined/null/empty values
      const cleanedMetadata: any = {};
      for (const [key, value] of Object.entries(options.metadata)) {
        // Only include if value is not undefined, not null, and not empty string
        if (value !== undefined && value !== null && value !== '') {
          // Double check it's a valid type for Firestore
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            cleanedMetadata[key] = value;
          }
        }
      }
      // Only add metadata if it has at least one valid property
      if (Object.keys(cleanedMetadata).length > 0) {
        // Use JSON to deeply clean any nested undefined values
        const jsonString = JSON.stringify(cleanedMetadata);
        const finalMetadata = JSON.parse(jsonString);
        console.log('🔵 [invites] Adding cleaned metadata:', finalMetadata);
        inviteData.metadata = finalMetadata;
      } else {
        console.log('🔵 [invites] Metadata was empty after cleaning, not including it');
      }
    } catch (error) {
      console.error('🔴 [invites] Error cleaning metadata:', error);
      // Don't include metadata if there's an error
    }
  } else {
    console.log('🔵 [invites] No metadata provided or invalid format');
  }

  // Final safety check: remove any undefined values from the entire inviteData object
  // Use JSON serialization to deeply remove all undefined values
  const finalInviteData: any = {};
  for (const [key, value] of Object.entries(inviteData)) {
    if (value !== undefined) {
      // If it's an object (like metadata), clean it recursively
      if (value && typeof value === 'object' && !(value instanceof Date) && !value.toDate && !value.seconds) {
        try {
          // Use JSON to remove all undefined values
          const jsonStr = JSON.stringify(value);
          const cleaned = JSON.parse(jsonStr);
          // Verify no undefined values remain
          let hasUndefined = false;
          for (const v of Object.values(cleaned)) {
            if (v === undefined) {
              hasUndefined = true;
              break;
            }
          }
          if (!hasUndefined && Object.keys(cleaned).length > 0) {
            finalInviteData[key] = cleaned;
          }
        } catch (e) {
          console.error('🔴 [invites] Error cleaning object:', key, e);
          // Skip this field if there's an error
        }
      } else {
        // For non-object values, include them directly
        finalInviteData[key] = value;
      }
    }
  }

  // One final pass: remove metadata entirely if it has any issues
  if (finalInviteData.metadata) {
    try {
      // Try to serialize/deserialize to catch any hidden undefined values
      const test = JSON.stringify(finalInviteData.metadata);
      JSON.parse(test);
    } catch (e) {
      console.error('🔴 [invites] Metadata failed validation, removing it:', e);
      delete finalInviteData.metadata;
    }
  }

  console.log('🔵 [invites] Final invite data being saved:', finalInviteData);
  console.log('🔵 [invites] Metadata in final data:', finalInviteData.metadata);
  
  await setDoc(doc(db, 'invites', token), finalInviteData);

  return token;
};

/**
 * Resolve an invite token
 */
export const resolveInviteToken = async (
  token: string
): Promise<InviteToken | null> => {
  const tokenDoc = await getDoc(doc(db, 'invites', token));

  if (!tokenDoc.exists()) {
    return null;
  }

  const data = tokenDoc.data();

  // Helper to safely convert Firestore Timestamp to Date
  const toDate = (timestamp: any): Date => {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    return new Date();
  };

  // Check if expired
  if (data.expiresAt) {
    const expiresAt = toDate(data.expiresAt);
    if (expiresAt < new Date()) {
      return null;
    }
  }

  // Convert timestamp to Date
  const inviteToken: InviteToken = {
    token,
    target: data.target,
    issuedBy: data.issuedBy,
    expiresAt: toDate(data.expiresAt),
    oneTime: data.oneTime,
    metadata: data.metadata,
    createdAt: toDate(data.createdAt),
  };

  // If one-time, delete it
  if (inviteToken.oneTime) {
    await deleteDoc(doc(db, 'invites', token));
  }

  return inviteToken;
};

/**
 * Generate invite URL
 */
export const generateInviteURL = (token: string): string => {
  const baseURL = window.location.origin;
  return `${baseURL}/i/${token}`;
};

/**
 * Generate QR code data URL (for display)
 * This is a simple placeholder - in production, use a QR code library
 */
export const generateQRCodeData = (url: string): string => {
  // For now, return URL - in production use qrcode library
  // import QRCode from 'qrcode';
  // return await QRCode.toDataURL(url);
  return url;
};



